import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { API, WHATSAPP_LINK, STATE_OPTIONS, DESIGNATION_OPTIONS, INTEREST_OPTIONS } from "@/lib/site";

const INITIAL = {
  full_name: "",
  organization: "",
  designation: "",
  city: "",
  state: "Madhya Pradesh",
  mobile: "",
  email: "",
  interest: "",
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-[#0B3C5C]/15 bg-white/80 px-4 py-3.5 text-sm text-[#0B3C5C] placeholder:text-[#7E9AAC] outline-none transition-all duration-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";

const labelCls = "font-mono-x mb-2 block text-[10px] uppercase tracking-[0.22em] text-[#4A6B82]";

export const EnquiryForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.interest) {
      toast.error("Please select what you are interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry submitted. Our team will get in touch with you shortly.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again or reach us on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" className="relative overflow-hidden bg-[#EAF3F8] py-24 sm:py-32">
      <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-[#5FA832]/5 blur-[120px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionTag index="12" label="Let's Talk" />
            <h2 className="font-display mt-6 text-2xl font-bold tracking-tight text-[#0B3C5C] sm:text-3xl lg:text-4xl">
              Let's Discuss Your Healthcare Requirement
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#4A6B82] sm:text-lg">
              Tell us what you are looking for and our team will get in touch.
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-10 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-7">
              <p className="font-display text-base font-bold text-[#0B3C5C]">Prefer WhatsApp?</p>
              <p className="mt-1 text-sm text-[#4A6B82]">
                Chat directly with our team for a quicker response.
              </p>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enquiry-cta-whatsapp"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-emerald-400 hover:shadow-lg hover:shadow-emerald-500/25"
              >
                <MessageCircle className="h-4 w-4" />
                Chat With Healthcare Solutions
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="lead-enquiry-form"
            className="rounded-[2rem] border border-[#0B3C5C]/10 bg-white/60 p-7 backdrop-blur-sm sm:p-10"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="f-name">Full Name *</label>
                <input id="f-name" data-testid="lead-form-input-name" required minLength={2}
                  placeholder="Enter your name" className={inputCls}
                  value={form.full_name} onChange={set("full_name")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-org">Organization / Hospital Name</label>
                <input id="f-org" data-testid="lead-form-input-org"
                  placeholder="Enter organization name" className={inputCls}
                  value={form.organization} onChange={set("organization")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-designation">Designation</label>
                <select id="f-designation" data-testid="lead-form-input-designation" className={inputCls}
                  value={form.designation} onChange={set("designation")}>
                  <option value="">Select designation</option>
                  {DESIGNATION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="f-city">City *</label>
                <input id="f-city" data-testid="lead-form-input-city" required minLength={2}
                  placeholder="Enter city" className={inputCls}
                  value={form.city} onChange={set("city")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-state">State *</label>
                <select id="f-state" data-testid="lead-form-select-state" required className={inputCls}
                  value={form.state} onChange={set("state")}>
                  {STATE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="f-mobile">Mobile Number *</label>
                <input id="f-mobile" data-testid="lead-form-input-mobile" required minLength={8}
                  type="tel" placeholder="Enter mobile number" className={inputCls}
                  value={form.mobile} onChange={set("mobile")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-email">Email Address *</label>
                <input id="f-email" data-testid="lead-form-input-email" required type="email"
                  placeholder="Enter email" className={inputCls}
                  value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-interest">I am interested in *</label>
                <select id="f-interest" data-testid="lead-form-select-interest" required className={inputCls}
                  value={form.interest} onChange={set("interest")}>
                  <option value="">Select requirement</option>
                  {INTEREST_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="f-message">Message</label>
                <textarea id="f-message" data-testid="lead-form-textarea-message" rows={4}
                  placeholder="Tell us briefly about your requirement" className={`${inputCls} resize-none`}
                  value={form.message} onChange={set("message")} />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-form-submit-button"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#5FA832] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F] hover:shadow-xl hover:shadow-[#5FA832]/25 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? "Submitting..." : "Submit Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
